import { Component, EventEmitter, OnInit, OnChanges, SimpleChanges, Input, Output, Pipe, PipeTransform } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';

import { DataRequestService } from '../../service/data-request.service';
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';
import { ProjectService } from 'src/app/Services/project.service';
import Swal from 'sweetalert2';
import moment from "moment";
import { currentPageSize, pageSizeOption } from 'src/app/config';
import { DATA_REQUEST_STATUSES_MAPPING } from '../../data-manage.config';

@Component({
  selector: 'app-request-table',
  templateUrl: './request-table.component.html',
  styleUrl: './request-table.component.css',
  standalone: false
})
export class RequestTableComponent implements OnInit, OnChanges {
  readonly genericFailedMessage = 'An unexpected error occurred. Please try again.';

  @Output() requestToOpenDetailsSidenav: EventEmitter<any> = new EventEmitter<any>();
  @Output() showAutomatedChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() filters: any = { search: '', status: 'All Statuses', action: 'All Actions' };

  allData: any[] = [];
  tableData: any[] = [];
  teamList: any[] = [];
  isLoading = false;

  // Pagination properties
  pageSize = currentPageSize;
  pageIndex = 0;
  totalItems = 0;
  pageSizeOptions = pageSizeOption;


  constructor(
    private dataRequestService: DataRequestService,
    private tokenEncryptionService: TokenEncryptionService,
    private projectService: ProjectService
  ) { }

  ngOnInit(): void {
    this.getTeamList();
    this.fetchRequests();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters'] && this.allData.length > 0) {
      this.pageIndex = 0; // Reset to first page on filter change
      this.applyFilters();
    }

  }

  getTeamList() {
    this.projectService.getTeamList().subscribe((res: any) => {
      this.teamList = res || [];
    });
  }

  fetchRequests() {
    this.isLoading = true;
    const user = this.tokenEncryptionService.getEncryptedItem<any>('logedInUser');
    const companyId = user?.company_id;
    this.dataRequestService.getAllRequests(companyId).subscribe({
      next: (res) => {
        if (res.success) {
          this.allData = res.data;
          this.applyFilters();
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to fetch requests', err);
        this.isLoading = false;
      }
    });
  }

  applyFilters() {
    let filtered = [...this.allData];

    // 0) Hide automated/system jobs by default
    const showAutomated = !!this.filters.showAutomated;
    if (!showAutomated) {
      filtered = filtered.filter(f => (f.source || 'Admin') !== 'System');
    }

    // 1. Search Filter
    if (this.filters.search) {
      const s = this.filters.search.toLowerCase();
      filtered = filtered.filter(f => {
        const employeeMatch = f.employee_name && f.employee_name.toLowerCase().includes(s);
        const teamMatch = f.request_type === 'Team' && this.getTeamName(f.team_id).toLowerCase().includes(s);
        const entireOrgMatch = (!f.employee_id && f.request_type !== 'Team' && 'entire org'.includes(s));
        return employeeMatch || teamMatch || entireOrgMatch;
      });
    }

    // Status mapping based on backend DB values to expected view names:
    const mapStatus: Record<string, string> = {
      'Queued': 'Pending',
      'Running': 'Processing',
    };

    // 2. Status Filter
    if (this.filters.status && this.filters.status !== 'All Statuses') {
      filtered = filtered.filter(f => {
        const currentMapped = mapStatus[f.status] || f.status;
        return currentMapped === this.filters.status;
      });
    }

    // 3. Action Filter
    if (this.filters.action && this.filters.action !== 'All Actions') {
      filtered = filtered.filter(f => f.action && f.action.toLowerCase() === this.filters.action.toLowerCase());
    }

    // Sort by created_at in descending order (newest first)
    filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    this.totalItems = filtered.length;
    const startIndex = this.pageIndex * this.pageSize;
    const pagedData = filtered.slice(startIndex, startIndex + this.pageSize);


    this.tableData = pagedData.map((r: any) => {
      const mapped = {
        ...r,
        name: r.request_type === 'Team'
          ? this.getTeamName(r.team_id)
          : (r.employee_id ? (r.employee_name || 'Unknown User') : 'Entire Organization'),
        isSystemJob: (r.source || 'Admin') === 'System',
        created_at: moment(r.created_at).format('MMM D, h:mm A'),
        startDate: moment(r.date_from).format('MMM D, YYYY'),
        endDate: moment(r.date_to).format('MMM D, YYYY'),
        range: `${moment(r.date_from).format('MMM D, YYYY')} → ${moment(r.date_to).format('MMM D, YYYY')}`,
        type: r.type || 'Screenshots',
        action: r.action,
        status: mapStatus[r.status] || r.status,
        download_link: r.download_link,
        link_expiring_at: r.link_expiring_at,
      };
      mapped.range = this.getRangeLabel(mapped);
      return mapped;
    });
  }

  onToggleShowAutomated(show: boolean) {
    this.showAutomatedChanged.emit(!!show);
  }

  getTeamName(teamId: any): string {
    if (!teamId) return 'Unknown Team';
    const team = this.teamList.find(t => t.id == teamId);
    return team?.title?.rendered || 'Unknown Team';
  }

  isDownloadLinkExpired(row: any): boolean {
    const expiresAt = row?.link_expiring_at;
    if (!expiresAt) return false;
    const exp = moment(expiresAt);
    if (!exp.isValid()) return false;
    return exp.isBefore(moment());
  }

  isDownloadableRow(row: any): boolean {
    return !!(
      row &&
      row.action === 'Download' &&
      row.status === 'Completed' &&
      row.download_link &&
      row.download_link !== 'NO_SCREENSHOT_AVAILABLE' &&
      row.download_link !== 'NO_LOGS_AVAILABLE'
    );
  }

  private parsePayload(payload: unknown): Record<string, unknown> | null {
    if (payload == null) return null;
    if (typeof payload === 'object') return payload as Record<string, unknown>;
    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload) as Record<string, unknown>;
      } catch {
        return null;
      }
    }
    return null;
  }

  private parseRetentionScope(payload: unknown): 'logs' | 'screenshots' | null {
    const s = this.parsePayload(payload)?.scope;
    return s === 'logs' || s === 'screenshots' ? s : null;
  }

  private isRollupRefreshRow(row: any): boolean {
    return (row?.type || '').toString() === 'RollupRefresh';
  }

  getRangeLabel(row: any): string {
    if (!this.isRollupRefreshRow(row)) {
      return row?.range || '';
    }
    const triggered = moment(row.date_from).format('MMM D, YYYY');
    const summary = row?.job?.logs_history?.rollup;
    if (summary?.days_built && summary?.last_built_date) {
      return `Triggered ${triggered} · ${summary.days_built}d rebuilt`;
    }
    return `Triggered ${triggered}`;
  }

  getActionLabel(row: any): string {
    const type = (row?.type || '').toString().trim();
    const isSystem = !!row?.isSystemJob;

    if (this.isRollupRefreshRow(row)) {
      const p = this.parsePayload(row?.payload);
      if (p?.backfill) return 'PT rollup backfill';
      return 'PT rollup refresh';
    }

    // System retention jobs: avoid exposing raw enum names in the UI.
    if (isSystem && type === 'RetentionCleanup' && row?.action === 'Delete') {
      const scope = this.parseRetentionScope(row?.payload);
      if (scope === 'logs') return 'Retention cleanup (Logs)';
      if (scope === 'screenshots') return 'Retention cleanup (Screenshots)';
      return 'Retention cleanup';
    }

    const typeLabel = type ? type : 'Data';
    if (row?.action === 'Download') return `Export ${typeLabel}`;
    if (row?.action === 'Delete') return `Delete ${typeLabel}`;
    return row?.action || '—';
  }

  onPaginateChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.applyFilters();
  }


  cancelRequest(id: number) {
    Swal.fire({
      title: 'Are you sure?',
      text: "You want to cancel this request?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, cancel it!'
    }).then((result) => {
      if (result.isConfirmed) {
        const user = this.tokenEncryptionService.getEncryptedItem<any>('logedInUser');
        const companyId = user?.company_id || 1;
        this.dataRequestService.cancelRequest(id, companyId).subscribe({
          next: (res) => {
            if (res.success) {
              Swal.fire('Cancelled!', 'Request has been cancelled.', 'success');
              this.fetchRequests();
            }
          },
          error: (err) => {
            Swal.fire('Error', err.error?.message || 'Failed to cancel', 'error');
          }
        });
      }
    });
  }

  deleteRequest(id: number) {
    Swal.fire({
      title: 'Are you sure?',
      text: "You want to delete this request permanently?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        const user = this.tokenEncryptionService.getEncryptedItem<any>('logedInUser');
        const companyId = user?.company_id || 1;
        this.dataRequestService.deleteRequest(id, companyId).subscribe({
          next: (res) => {
            if (res.success) {
              Swal.fire('Deleted!', 'Request has been deleted.', 'success');
              this.fetchRequests();
            }
          },
          error: (err) => {
            Swal.fire('Error', err.error?.message || 'Failed to delete', 'error');
          }
        });
      }
    });
  }

  requestToDetail(row: any) {
    this.requestToOpenDetailsSidenav.emit(row);
  }
}

@Pipe({
  name: 'statusClass',
  standalone: true // Makes it easy to use "locally"
})
export class StatusClassPipe implements PipeTransform {
  transform(status: string): string {
    return DATA_REQUEST_STATUSES_MAPPING[status]?.color || 'bg-gray-50 text-gray-600 border-gray-300';
  }
}
